// @ts-nocheck
import { IconPlus, IconCurrencyDollar } from '@tabler/icons-react';
import { Avatar, Badge, Box, Button, Card, Group, ScrollArea, Stack, Text, Title } from '@/components/ui';
import { mockDeals, Deal } from '../mockData/crm';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

interface PipelineColumn {
  id: Deal['stage'];
  title: string;
  color: string;
}

const columns: PipelineColumn[] = [
  { id: 'lead', title: 'Lead', color: 'gray' },
  { id: 'qualified', title: 'Qualified', color: 'blue' },
  { id: 'proposal', title: 'Proposal', color: 'cyan' },
  { id: 'negotiation', title: 'Negotiation', color: 'orange' },
  { id: 'closed-won', title: 'Closed Won', color: 'green' },
  { id: 'closed-lost', title: 'Closed Lost', color: 'red' },
];

const DealCard = ({ deal }: { deal: Deal }) => (
  <Card p="sm" style={{ cursor: 'grab' }}>
    <Stack gap="xs">
      <Group justify="space-between">
        <Badge size="xs" color={
          deal.priority === 80 ? 'green' :
          deal.priority >= 60 ? 'cyan' :
          deal.priority >= 40 ? 'yellow' : 'gray'
        }>
          {deal.probability}% prob
        </Badge>
        <Text size="xs" c="dimmed">{formatDate(deal.expectedCloseDate)}</Text>
      </Group>

      <Text size="sm" fw={500}>{deal.title}</Text>
      <Text size="xs" c="dimmed">{deal.company}</Text>

      <Group justify="space-between">
        <Group gap={4}>
          <IconCurrencyDollar size={14} />
          <Text size="sm" fw={600}>{formatCurrency(deal.value)}</Text>
        </Group>
        <Avatar size="sm" radius="xl" color="blue">
          {deal.ownerName.split(' ').map(n => n[0]).join('')}
        </Avatar>
      </Group>
    </Stack>
  </Card>
);

const PipelineColumn = ({ column, deals }: { column: PipelineColumn; deals: Deal[] }) => {
  const totalValue = deals.reduce((acc, d) => acc + d.value, 0);

  return (
    <Box
      style={{
        minWidth: 280,
        maxWidth: 280,
        backgroundColor: 'var(--mantine-color-gray-light)',
        borderRadius: 12,
        padding: 12,
      }}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="sm">
          <Box
            w={12}
            h={12}
            style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${column.color}-filled)` }}
          />
          <Text fw={600}>{column.title}</Text>
        </Group>
        <Badge size="sm" variant="light" circle>
          {deals.length}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed" mb="md">{formatCurrency(totalValue)}</Text>

      <Stack gap="sm">
        {deals.map(deal => (
          <DealCard key={deal.id} deal={deal} />
        ))}
        {deals.length === 0 && (
          <Box ta="center" py="xl">
            <Text size="sm" c="dimmed">No deals</Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default function PipelinePage() {
  const getDealsByStage = (stage: Deal['stage']) => {
    return mockDeals.filter(d => d.stage === stage);
  };

  const totalPipeline = mockDeals.reduce((acc, d) => acc + d.value, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Sales Pipeline</Title>
          <Text c="dimmed" size="sm">
            {mockDeals.length} deals worth {formatCurrency(totalPipeline)}
          </Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Add Deal</Button>
      </Group>

      {/* Pipeline Board */}
      <ScrollArea>
        <Group align="flex-start" gap="md" wrap="nowrap" pb="md">
          {columns.map(column => (
            <PipelineColumn
              key={column.id}
              column={column}
              deals={getDealsByStage(column.id)}
            />
          ))}
        </Group>
      </ScrollArea>
    </Stack>
  );
}
